import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'
import FlexibleDialog, {
  propTypes as dialogPropTypes,
} from 'components/molecules/flexible-dialog'
import NewDropDownSelect from 'components/molecules/new-dropdown-select'
import {
  AdvertisementPortalIdEnum,
  MarktplaatsBulkActions,
} from 'config/enums'
import { media } from 'utilities/styled'
import LoadingButton from 'components/atoms/loading-button'
import { useDispatch } from 'react-redux'
import {
  createMarktplaatsBulkAction,
  publishPortal,
} from 'redux/actions/data'
import Typography from 'components/molecules/typography'
import toast from 'utilities/toast'

const SelectRow = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.sizings.lvl2};
  column-gap: ${({ theme }) => theme.sizings.lvl4};
  margin-top: ${({ theme }) => theme.sizings.lvl4};

  ${media.tablet`
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
  `}
`

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.sizings.lvl4};
`

const Intro = styled(Typography)`
  margin: 0;
  white-space: pre-line;
`

const Select = styled(NewDropDownSelect)`
  width: 100%;

  ${media.tablet`
    width: ${({ theme }) => theme.sizeByFactor(30)};
  `}
`

const MarktplaatsBulkActionsDialog = ({
  preselectedLocationId,
  onClose,
  selectedCarIds = [],
  unpublishedCarIds = [],
  ...restProps
}) => {
  const { t } = useTranslation()
  const [selectedBulkAction, setSelectedBulkAction] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const bulkActionItems = Object.values(MarktplaatsBulkActions).map(
    (value) => ({
      label: t(`marktplaatsBulkActionsDialog.actions.${value}`),
      value,
    }),
  )

  const dispatch = useDispatch()

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      if (unpublishedCarIds.length > 0) {
        await Promise.all(
          unpublishedCarIds.map((carId) =>
            dispatch(
              publishPortal('portalPublish', {
                auto_id: Number(carId),
                portal_ids: [Number(AdvertisementPortalIdEnum.MARKTPLAATS)],
              }),
            ),
          ),
        )
      }

      const data = await dispatch(
        createMarktplaatsBulkAction({
          vestiging_id:
            preselectedLocationId && preselectedLocationId !== '0'
              ? preselectedLocationId
              : null,
          action: selectedBulkAction,
          auto_ids: selectedCarIds.map(Number),
        }),
      )

      setIsSubmitting(false)
      if (data && data.success) {
        toast.success(t('marktplaatsBulkActionsDialog.success'))
        onClose && onClose()
      }
    } catch (error) {
      console.error(error)
      toast.error(t('marktplaatsBulkActionsDialog.publishError'))
      setIsSubmitting(false)
    }
  }

  return (
    <FlexibleDialog
      title={t('marktplaatsBulkActionsDialog.title')}
      disableBackdropClick={isSubmitting}
      onClose={onClose}
      content={
        <>
          <Intro type="ExplanationParagraph">
            {t('marktplaatsBulkActionsDialog.intro')}
          </Intro>
          <SelectRow>
            <Select
              label={t('marktplaatsBulkActionsDialog.actionLabel')}
              items={bulkActionItems}
              onChange={setSelectedBulkAction}
              value={selectedBulkAction}
              selectionRequired
              required
              filled
              data-test-e2e="select-action"
            />
          </SelectRow>
          <Typography type="BodyParagraph">
            {t('marktplaatsBulkActionsDialog.selectedCars', {
              count: selectedCarIds.length,
            })}
          </Typography>
          {unpublishedCarIds.length > 0 && (
            <Typography type="BodyParagraph">
              {t(unpublishedCarIds.length === 1
                ? 'marktplaatsBulkActionsDialog.willBePublishedSingle'
                : 'marktplaatsBulkActionsDialog.willBePublished', {
                count: unpublishedCarIds.length,
              })}
            </Typography>
          )}
          <Controls>
            <LoadingButton
              level="option"
              onClick={onClose}
              text={t('cancel')}
              disabled={isSubmitting}
              noPadding
              data-test-e2e="button-cancel"
            />
            <LoadingButton
              level="cta"
              isLoading={isSubmitting}
              disabled={!selectedBulkAction}
              type="submit"
              onClick={handleSubmit}
              data-test-e2e="button-save"
            >
              {t('marktplaatsBulkActionsDialog.executeAction')}
            </LoadingButton>
          </Controls>
        </>
      }
      {...restProps}
      data-test-e2e="marktplaats-bulk-actions-dialog"
    />
  )
}

const { title, content, ...dialogRestPropTypes } = dialogPropTypes

MarktplaatsBulkActionsDialog.propTypes = {
  ...dialogRestPropTypes,
  preselectedLocationId: PropTypes.string,
  selectedCarIds: PropTypes.array,
  unpublishedCarIds: PropTypes.array,
}

export default MarktplaatsBulkActionsDialog
